import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Flame, Send, Trash2, Info, X, AtSign, SmilePlus } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * Smack Stream (iter31m, Phase 1) — right-side sidebar widget where any league
 * participant can post a ≤128 char trash-talk message. Auto-listens for
 * websocket `smack_added` / `smack_deleted` events on the supplied socket ref
 * so the feed updates live without polling.
 *
 * Visibility: rendered only when `league.smack_stream_enabled !== false`.
 * Phase 1 = wall only. MVP voting + scoring is Phase 2.
 *
 * iter63z'' — @-mentions: in-composer autocomplete dropdown that surfaces
 * league participants when the user types `@`. Rendered mentions in the feed
 * are highlighted; mentions of the current user get a stronger heat treatment
 * plus a small "mentioned you" badge on the message row.
 */
const MAX_LEN = 128;
const FEED_LIMIT = 10;
const REACTION_EMOJIS = ["❤️", "😄", "🔥", "👎"];
const MENTION_TOKEN_RE = /@([A-Za-z0-9_.-]{1,30})/g;

// iter87.41 — Smack Stream timestamp with day-aware date label.
// Today  → "3:42 PM"
// Yday   → "3:42 PM - yesterday"
// Older  → "3:42 PM - Jul 23"
function formatSmackTimestamp(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diffDays = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
    if (diffDays <= 0) return time;
    if (diffDays === 1) return `${time} - yesterday`;
    return `${time} - ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

export default function SmackStreamCard({ leagueId, me, isAdmin, participants = [], refreshPing = 0 }) {
    const [enabled, setEnabled] = useState(true);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    // iter87.180.4 — Delete confirm modal (replaces window.confirm which
    // silently fails on iOS Safari inside async handlers — the trash icon
    // appeared to do nothing on the deployed beta).
    const [deleteTarget, setDeleteTarget] = useState(null);
    const feedRef = useRef(null);
    const textareaRef = useRef(null);

    // iter63z'' — @-mention autocomplete state. `start`/`end` describe the
    // slice of `text` (the leading `@` plus partial query) that gets replaced
    // when a suggestion is picked.
    const [mention, setMention] = useState({ active: false, query: "", start: 0, end: 0 });
    const [mentionIdx, setMentionIdx] = useState(0);

    const load = useCallback(async () => {
        try {
            const r = await api.listSmack(leagueId, FEED_LIMIT);
            setEnabled(!!r.enabled);
            setMessages(r.messages || []);
        } catch (e) {
            // Silent — non-critical sidebar
        }
    }, [leagueId]);

    useEffect(() => { load(); }, [load, refreshPing]);

    // Lookup map for rendering — used to validate `@tokens` so we don't
    // highlight random text that happens to start with `@`. Case-insensitive.
    const participantsByUsername = useMemo(() => {
        const m = new Map();
        for (const p of participants) {
            if (p?.username) m.set(p.username.toLowerCase(), p);
        }
        return m;
    }, [participants]);

    const mentionMatches = useMemo(() => {
        if (!mention.active) return [];
        const q = mention.query.toLowerCase();
        const myId = me?.id;
        // iter63z'''' — Surface reserved broadcast aliases as virtual
        // suggestions ("everyone in this Tournament" and "league admins").
        // They prefix-match on q just like regular usernames so they only
        // appear when the user is actually typing toward them.
        const aliases = [
            {
                id: "__alias_channel__",
                username: "channel",
                is_alias: true,
                label: "channel",
                hint: "Everyone in this Tournament (max 1/day)",
            },
            {
                id: "__alias_admins__",
                username: "admins",
                is_alias: true,
                label: "admins",
                hint: "League admin + co-admins",
            },
        ];
        const aliasMatches = aliases.filter((a) => a.username.startsWith(q));
        const userMatches = participants
            .filter((p) => p?.username && p.id !== myId)
            .filter((p) => p.username.toLowerCase().startsWith(q));
        return [...aliasMatches, ...userMatches].slice(0, 6);
    }, [mention, participants, me]);

    // Clamp the highlighted index whenever the suggestions shrink.
    useEffect(() => {
        if (mentionIdx >= mentionMatches.length && mentionMatches.length > 0) setMentionIdx(0);
    }, [mentionMatches.length, mentionIdx]);

    const remaining = MAX_LEN - text.length;
    const tooLong = remaining < 0;

    const detectMention = (value, caret) => {
        const before = value.slice(0, caret);
        // Either start-of-string or whitespace immediately before `@token`.
        const m = before.match(/(?:^|\s)@([A-Za-z0-9_.-]{0,30})$/);
        if (m) {
            const query = m[1];
            const atIdx = before.length - query.length - 1;
            setMention({ active: true, query, start: atIdx, end: caret });
            setMentionIdx(0);
        } else if (mention.active) {
            setMention({ active: false, query: "", start: 0, end: 0 });
        }
    };

    const onTextChange = (e) => {
        const v = e.target.value;
        setText(v);
        detectMention(v, e.target.selectionStart ?? v.length);
    };

    const onTextareaSelect = (e) => {
        // User clicked / arrowed into a different position — reassess.
        detectMention(text, e.target.selectionStart ?? text.length);
    };

    const insertMention = (p) => {
        const before = text.slice(0, mention.start);
        const after = text.slice(mention.end);
        // Trailing space so the user can keep typing immediately.
        const inserted = `@${p.username} `;
        const newText = `${before}${inserted}${after}`;
        setText(newText);
        setMention({ active: false, query: "", start: 0, end: 0 });
        // Restore caret to just after the inserted mention (incl. trailing space).
        requestAnimationFrame(() => {
            const ta = textareaRef.current;
            if (ta) {
                const newPos = before.length + inserted.length;
                ta.focus();
                ta.setSelectionRange(newPos, newPos);
            }
        });
    };

    const onKeyDown = (e) => {
        if (mention.active && mentionMatches.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIdx((i) => (i + 1) % mentionMatches.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                insertMention(mentionMatches[mentionIdx] || mentionMatches[0]);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setMention({ active: false, query: "", start: 0, end: 0 });
                return;
            }
        }
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e);
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const trimmed = text.trim();
        if (!trimmed) return;
        if (trimmed.length > MAX_LEN) { toast.error(`Keep it under ${MAX_LEN} chars`); return; }
        setBusy(true);
        try {
            await api.postSmack(leagueId, trimmed);
            setText("");
            setMention({ active: false, query: "", start: 0, end: 0 });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't post");
        }
        setBusy(false);
    };

    const remove = (msg) => {
        // iter87.180.4 — Open a React confirm modal instead of using
        // window.confirm inside an async handler (iOS Safari silently
        // suppresses async confirms, which was the beta bug).
        setDeleteTarget(msg);
    };

    const confirmDelete = async () => {
        const msg = deleteTarget;
        if (!msg) return;
        try {
            await api.deleteSmack(leagueId, msg.id);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't delete");
        } finally {
            setDeleteTarget(null);
        }
    };

    // iter43e — Toggle an emoji reaction. Optimistic-ish: we patch the
    // single message in-place from the server response so we don't refetch
    // the whole feed for every click.
    const toggleReaction = async (msg, emoji) => {
        if (!me) {
            toast.message("Sign in to react");
            return;
        }
        try {
            const r = await api.reactSmack(leagueId, msg.id, emoji);
            setMessages((prev) => prev.map((m) =>
                m.id === msg.id ? { ...m, reactions: r.reactions } : m
            ));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't react");
        }
    };

    if (!enabled) return null;

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="smack-stream-card">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Flame className="w-4 h-4 text-heat shrink-0" />
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight truncate">Smack Stream</h3>
                </div>
                <button
                    type="button"
                    onClick={() => setInfoOpen(true)}
                    className="brutal-btn-ghost p-1.5"
                    data-testid="smack-info-btn"
                    title="What is Smack Stream?"
                >
                    <Info className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Compose — only for participants (i.e. when `me` is set) */}
            {me ? (
                <form onSubmit={submit} className="space-y-1.5" data-testid="smack-compose-form">
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            data-testid="smack-textarea"
                            value={text}
                            onChange={onTextChange}
                            onSelect={onTextareaSelect}
                            onKeyDown={onKeyDown}
                            onBlur={() => {
                                // Defer closing so a click on a suggestion can register first.
                                setTimeout(() => setMention((s) => s.active ? { active: false, query: "", start: 0, end: 0 } : s), 120);
                            }}
                            placeholder="🔥 Talk your sh** — 128 chars. Type @ to tag."
                            rows={2}
                            maxLength={MAX_LEN + 30}
                            className="brutal-input w-full text-xs resize-y"
                        />
                        {mention.active && mentionMatches.length > 0 && (
                            <div
                                data-testid="mention-autocomplete"
                                className="absolute left-0 right-0 top-full mt-1 z-30 brutal-card-light border-2 border-cyber/60 p-1 max-h-44 overflow-y-auto shadow-lg"
                            >
                                {mentionMatches.map((p, idx) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        data-testid={`mention-suggestion-${p.username}`}
                                        // Mousedown (not click) so the textarea onBlur doesn't fire first
                                        // and tear down the dropdown before the click registers.
                                        onMouseDown={(ev) => { ev.preventDefault(); insertMention(p); }}
                                        onMouseEnter={() => setMentionIdx(idx)}
                                        className={
                                            "w-full text-left text-[11px] px-2 py-1.5 flex items-center gap-2 " +
                                            (idx === mentionIdx
                                                ? (p.is_alias ? "bg-heat/20 text-heat" : "bg-cyber/20 text-cyber")
                                                : "text-zinc-300 hover:bg-[#222]")
                                        }
                                    >
                                        <AtSign className={"w-3 h-3 shrink-0 " + (p.is_alias ? "opacity-100" : "opacity-70")} />
                                        <span className="font-bold truncate">{p.username}</span>
                                        {p.is_alias ? (
                                            <span className="ml-auto text-[9px] uppercase tracking-widest text-zinc-500 truncate">{p.hint}</span>
                                        ) : (
                                            p.is_admin && (
                                                <span className="ml-auto text-[9px] uppercase tracking-widest text-cyber/80">Admin</span>
                                            )
                                        )}
                                    </button>
                                ))}
                                <p className="text-[9px] text-zinc-500 px-2 pt-1 pb-0.5 leading-none">
                                    ↑ ↓ to navigate · Enter to tag · Esc to close
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <span
                            data-testid="smack-counter"
                            className={`text-[10px] ${tooLong ? "text-heat font-bold" : "text-zinc-500"}`}
                        >
                            {remaining} left
                        </span>
                        <button
                            type="submit"
                            data-testid="smack-submit-btn"
                            disabled={busy || !text.trim() || tooLong}
                            className="brutal-btn-primary text-[10px] py-1.5 px-2.5 disabled:opacity-30"
                        >
                            <Send className="w-3 h-3" /> Smack
                        </button>
                    </div>
                </form>
            ) : (
                <p className="text-[11px] text-zinc-500 italic">Join the rivalry to drop a smack.</p>
            )}

            {/* Feed */}
            <div
                ref={feedRef}
                data-testid="smack-feed"
                className="space-y-1.5 max-h-72 overflow-y-auto pr-1"
            >
                {messages.length === 0 && (
                    <p className="text-[11px] text-zinc-600 italic">No smack yet — be the spark.</p>
                )}
                {messages.map((m) => {
                    const isMine = me && m.participant_id === me.id;
                    const canDelete = isMine || isAdmin;
                    const mentionsMe = !!(me && Array.isArray(m.mentions) && m.mentions.includes(me.id));
                    return (
                        <div
                            key={m.id}
                            data-testid={`smack-message-${m.id}`}
                            className={
                                "text-[11px] p-2 border bg-ink " +
                                (mentionsMe
                                    ? "border-heat/70 bg-heat/[0.06]"
                                    : isMine
                                        ? "border-cyber/40"
                                        : "border-[#222]")
                            }
                        >
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="flex items-center gap-1.5 min-w-0">
                                    {/* iter84.24 — Author avatar inline with the username,
                                        looked up from participants so it stays current
                                        when the user edits their identity. */}
                                    {(() => {
                                        const author = m.username ? participantsByUsername.get(m.username.toLowerCase()) : null;
                                        const av = author?.avatar_emoji;
                                        return av ? (
                                            <span
                                                className="text-sm leading-none shrink-0"
                                                data-testid={`smack-avatar-${m.id}`}
                                                aria-hidden
                                            >{av}</span>
                                        ) : null;
                                    })()}
                                    <span className={`font-bold text-[10px] uppercase tracking-wider truncate ${isMine ? "text-cyber" : "text-zinc-400"}`}>
                                        {m.username}
                                    </span>
                                    {mentionsMe && (
                                        <span
                                            data-testid={`smack-mentioned-you-${m.id}`}
                                            className="inline-flex items-center gap-0.5 text-[8px] uppercase font-extrabold tracking-widest px-1 py-0.5 bg-heat/20 text-heat border border-heat/50 leading-none"
                                            title="You were tagged in this smack"
                                        >
                                            <AtSign className="w-2 h-2" /> You
                                        </span>
                                    )}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className="text-[9px] text-zinc-600" title={new Date(m.created_at).toLocaleString()}>
                                        {formatSmackTimestamp(m.created_at)}
                                    </span>
                                    {canDelete && (
                                        <button
                                            type="button"
                                            data-testid={`smack-delete-${m.id}-btn`}
                                            onClick={() => remove(m)}
                                            className="brutal-btn-ghost p-0.5"
                                            title={isMine ? "Delete your message" : "Admin: delete message"}
                                        >
                                            <Trash2 className="w-2.5 h-2.5 text-heat" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p
                                data-testid={`smack-body-${m.id}`}
                                className="text-zinc-300 leading-snug mt-1 break-words"
                            >
                                {renderBodyWithMentions(m.body, participantsByUsername, me?.id)}
                            </p>
                            <ReactionBar
                                msg={m}
                                me={me}
                                onToggle={toggleReaction}
                            />
                        </div>
                    );
                })}
            </div>

            {infoOpen && (
                <button
                    type="button"
                    aria-label="Close info"
                    className="fixed inset-0 z-40 bg-black/70 cursor-default"
                    onClick={() => setInfoOpen(false)}
                />
            )}
            {infoOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                    <div className="brutal-card p-6 max-w-md w-full pointer-events-auto" data-testid="smack-info-modal">
                        <div className="flex items-start justify-between gap-2 mb-3">
                            <h3 className="font-display font-black text-xl flex items-center gap-2">
                                <Flame className="w-5 h-5 text-heat" /> Burn your Rivals
                            </h3>
                            <button
                                type="button"
                                onClick={() => setInfoOpen(false)}
                                className="brutal-btn-ghost p-1.5"
                                data-testid="smack-info-close-btn"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                            <p>The Smack Stream is your rivalry's trash-talk wall. Use it for hot takes, gloating, and ruthless predictions throughout the tournament.</p>
                            <ul className="text-xs text-zinc-400 space-y-1 list-disc list-inside">
                                <li>Max <strong className="text-cyber">128 characters</strong> per post.</li>
                                <li>Limit <strong className="text-cyber">5 messages</strong> every 30 minutes.</li>
                                <li>Type <strong className="text-cyber">@</strong> to tag a crewmate — they get a heads-up notification.</li>
                                <li>Need to reach everyone? <strong className="text-heat">@channel</strong> tags the whole Tournament (limit: once per day). <strong className="text-heat">@admins</strong> pings the rivalry admin + co-admins.</li>
                                <li>You can delete your own. The rivalry admin can delete anyone's.</li>
                                <li>Be ruthless but not mean. Your rivalry admin can switch the stream off if it crosses the line.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* iter87.180.4 — Delete confirm modal (replaces window.confirm
                which iOS Safari silently suppresses inside async handlers). */}
            {deleteTarget && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                    data-testid="smack-delete-confirm-backdrop"
                    onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}
                >
                    <div
                        className="brutal-card max-w-sm w-full p-5 space-y-4"
                        data-testid="smack-delete-confirm-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div>
                            <h3 className="font-display font-black text-lg uppercase tracking-tight">
                                Delete this smack?
                            </h3>
                            <p className="text-xs text-zinc-400 mt-2 italic break-words">
                                "{deleteTarget.body}"
                            </p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(null)}
                                data-testid="smack-delete-confirm-cancel-btn"
                                className="brutal-btn-ghost text-xs py-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                data-testid="smack-delete-confirm-btn"
                                className="brutal-btn text-xs py-2 bg-heat text-black border-black shadow-brutal"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


/**
 * Walk the message body and wrap recognised `@username` tokens (case-
 * insensitive) in highlight spans. Unknown `@blah` text is left as-is so
 * we don't accidentally style email-ish strings or stray @ chars.
 *
 * iter63z'''' — Also recognises reserved aliases `@channel` and `@admins`
 * and styles them in the heat palette so broadcast tags pop visually
 * regardless of whether the viewer was personally tagged.
 */
function renderBodyWithMentions(body, participantsByUsername, mePid) {
    if (!body) return body;
    const out = [];
    let last = 0;
    // Fresh regex per call — needed because we mutate lastIndex.
    const re = new RegExp(MENTION_TOKEN_RE.source, "g");
    let m;
    while ((m = re.exec(body)) !== null) {
        const tokenLower = m[1].toLowerCase();
        const isAlias = tokenLower === "channel" || tokenLower === "admins";
        const matched = isAlias ? null : (participantsByUsername?.get(tokenLower) || null);
        if (!isAlias && !matched) continue;
        if (m.index > last) out.push(body.slice(last, m.index));
        if (isAlias) {
            out.push(
                <span
                    key={`mn-${m.index}`}
                    data-testid={`mention-alias-${tokenLower}`}
                    className="font-extrabold text-heat bg-heat/15 px-0.5 rounded-sm uppercase tracking-tight"
                >
                    @{tokenLower}
                </span>
            );
        } else {
            const isMe = matched.id === mePid;
            out.push(
                <span
                    key={`mn-${m.index}`}
                    data-testid={`mention-token-${matched.username}`}
                    className={
                        isMe
                            ? "font-extrabold text-heat bg-heat/15 px-0.5 rounded-sm"
                            : "font-semibold text-cyber"
                    }
                >
                    @{matched.username}
                </span>
            );
        }
        last = m.index + m[0].length;
    }
    if (last === 0) return body;
    if (last < body.length) out.push(body.slice(last));
    return out;
}


/**
 * iter84.12 — Reactions row: chat-style popover picker + counted pills only.
 *
 * Before: 4 static pills always rendered (cluttered when empty).
 * Now:
 *   - Pills render ONLY for emojis with ≥1 reaction (clean default state).
 *   - A small `+ smile` button opens a Popover with the 4 emoji choices.
 *   - Users CANNOT react to their own messages (`isOwn` hides everything
 *     that would let them add a reaction; existing counts from others
 *     stay visible but are non-interactive on their own posts).
 *   - Spectators (no `me`) see counts but can't toggle.
 */
function ReactionBar({ msg, me, onToggle }) {
    const reactions = msg.reactions || {};
    const isOwn = !!(me && msg.participant_id === me.id);
    const canReact = !!me && !isOwn;
    const [pickerOpen, setPickerOpen] = useState(false);

    // Only render pills for emojis that actually have reactions (cleaner UI).
    const activeEmojis = REACTION_EMOJIS.filter((e) => (reactions[e] || []).length > 0);
    const hasAny = activeEmojis.length > 0;

    const pick = (emoji) => {
        setPickerOpen(false);
        onToggle(msg, emoji);
    };

    // Nothing to show at all (no reactions yet + can't add) → hide whole row.
    if (!hasAny && !canReact) return null;

    return (
        <div
            className="flex items-center gap-1 mt-1.5 flex-wrap"
            data-testid={`smack-reactions-${msg.id}`}
        >
            {activeEmojis.map((emoji) => {
                const pids = reactions[emoji] || [];
                const count = pids.length;
                const byMe = !!(me && pids.includes(me.id));
                // Clicking counted pills on others' posts toggles your reaction;
                // on your own posts the pill is informational only (no toggle).
                return (
                    <button
                        key={emoji}
                        type="button"
                        data-testid={`smack-react-${msg.id}-${emoji}`}
                        onClick={() => canReact && onToggle(msg, emoji)}
                        disabled={!canReact}
                        title={
                            isOwn
                                ? "You can't react to your own post"
                                : byMe
                                    ? "Remove your reaction"
                                    : canReact
                                        ? "Toggle reaction"
                                        : "Sign in to react"
                        }
                        className={
                            "text-[11px] leading-none px-1.5 py-0.5 border rounded transition-colors " +
                            (byMe
                                ? "bg-heat/15 border-heat/60 text-heat"
                                : "border-[#333] text-zinc-400 " + (canReact ? "hover:border-heat/40 hover:text-zinc-100" : "")) +
                            (!canReact ? " opacity-70 cursor-default" : " cursor-pointer")
                        }
                    >
                        <span>{emoji}</span>
                        <span className="ml-0.5 font-mono text-[10px]">{count}</span>
                    </button>
                );
            })}
            {canReact && (
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            data-testid={`smack-reactpicker-${msg.id}`}
                            title="Add reaction"
                            className="text-zinc-500 hover:text-heat border border-[#333] hover:border-heat/40 rounded-full p-0.5 transition-colors cursor-pointer leading-none"
                        >
                            <SmilePlus className="w-3 h-3" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        side="top"
                        sideOffset={6}
                        className="w-auto p-1.5 bg-ink border-2 border-[#333] flex items-center gap-1"
                        data-testid={`smack-reactpicker-popover-${msg.id}`}
                    >
                        {REACTION_EMOJIS.map((emoji) => {
                            const byMe = !!(me && (reactions[emoji] || []).includes(me.id));
                            return (
                                <button
                                    key={emoji}
                                    type="button"
                                    data-testid={`smack-reactpicker-${msg.id}-${emoji}`}
                                    onClick={() => pick(emoji)}
                                    title={byMe ? `Remove ${emoji}` : `React with ${emoji}`}
                                    className={
                                        "text-base leading-none px-2 py-1 rounded transition-transform hover:scale-125 cursor-pointer " +
                                        (byMe ? "bg-heat/20 ring-1 ring-heat/60" : "hover:bg-[#222]")
                                    }
                                >
                                    {emoji}
                                </button>
                            );
                        })}
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}
